import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { selectActiveVisitors } from "@/redux/slices/visitorsSlice";
import { selectCallRequests } from "@/redux/slices/callRequestsSlice";
import { removeCallRequest, type CallRequest } from "@/redux/slices/callRequestsSlice";
import { addErrorToast, addSuccessToast } from "@/redux/slices/toastSlice";
import { getSocketService } from "@/lib/utils/socket";
import { useCallManagement } from "@/lib/hooks/useCallManagement";
import Badge from "@/components/view/dashboard/@root/Badge";
import EmptyState from "@/components/view/dashboard/@root/EmptyState";
import Tooltip from "@/components/ui/tooltip/Tooltip";

const STORAGE_KEY = "reacho-live-deck-position";
const DEFAULT_POSITION = { x: 0, y: 0 };
const PANEL_WIDTH = 420;
const PANEL_MIN_HEIGHT = 360;
const PANEL_MAX_HEIGHT = 85; // vh
const VIEWPORT_PADDING = 16;

/** Keeps the panel fully inside the visible viewport. */
function clampPositionToViewport(x: number, y: number): { x: number; y: number } {
  if (typeof window === "undefined") return { x, y };
  const padding = VIEWPORT_PADDING;
  const maxPanelHeight = Math.min(window.innerHeight * (PANEL_MAX_HEIGHT / 100), 720);
  const minX = padding;
  const maxX = Math.max(minX, window.innerWidth - PANEL_WIDTH - padding);
  const minY = padding;
  const maxY = Math.max(
    minY,
    window.innerHeight - Math.min(PANEL_MIN_HEIGHT, maxPanelHeight) - padding
  );
  return {
    x: Math.round(Math.max(minX, Math.min(maxX, x))),
    y: Math.round(Math.max(minY, Math.min(maxY, y))),
  };
}

function loadStoredPosition(): { x: number; y: number } {
  if (typeof window === "undefined") return DEFAULT_POSITION;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_POSITION;
    const parsed = JSON.parse(raw) as { x: number; y: number };
    if (typeof parsed.x === "number" && typeof parsed.y === "number") {
      return clampPositionToViewport(parsed.x, parsed.y);
    }
  } catch {
    // ignore
  }
  return DEFAULT_POSITION;
}

function getDefaultPosition(): { x: number; y: number } {
  const padding = 24;
  return clampPositionToViewport(
    window.innerWidth - PANEL_WIDTH - padding,
    Math.max(padding, (window.innerHeight - 560) / 2)
  );
}

const formatTimeAgo = (isoDate: string) => {
  const diff = Date.now() - new Date(isoDate).getTime();
  if (diff < 60000) return "just now";
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
};

// ----- Live deck panel (content + drag) -----
function LiveDeckPanel({
  onClose,
  initialPosition,
  onPositionChange,
}: {
  onClose: () => void;
  initialPosition: { x: number; y: number };
  onPositionChange: (pos: { x: number; y: number }) => void;
}) {
  const dispatch = useAppDispatch();
  const callRequests = useAppSelector(selectCallRequests);
  const activeVisitors = useAppSelector(selectActiveVisitors);
  const { initiateCall } = useCallManagement();
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(() =>
    clampPositionToViewport(initialPosition.x, initialPosition.y)
  );
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, left: 0, top: 0 });
  const positionRef = useRef(position);
  positionRef.current = position;
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"calls" | "visitors">("calls");
  const isClosingRef = useRef(false);

  const handleMinimize = useCallback(() => {
    if (!panelRef.current || isClosingRef.current) return;
    isClosingRef.current = true;
    gsap.to(panelRef.current, {
      scale: 0.92,
      opacity: 0,
      y: 12,
      duration: 0.18,
      ease: "power2.in",
      onComplete: () => {
        onClose();
        isClosingRef.current = false;
      },
    });
  }, [onClose]);

  const sortedRequests = useMemo(
    () =>
      [...callRequests].sort(
        (a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()
      ),
    [callRequests]
  );

  // GSAP: open animation (scale + opacity)
  useGSAP(
    () => {
      if (!panelRef.current) return;
      gsap.fromTo(
        panelRef.current,
        {
          scale: 0.92,
          opacity: 0,
          y: 12,
        },
        {
          scale: 1,
          opacity: 1,
          y: 0,
          duration: 0.22,
          ease: "power3.out",
          overwrite: true,
        }
      );
    },
    { scope: panelRef, dependencies: [] }
  );

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if ((e.target as HTMLElement).closest("button, a, [role=button]")) return;
      setIsDragging(true);
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        left: position.x,
        top: position.y,
      };
    },
    [position]
  );

  // Keep panel in viewport when window is resized
  useEffect(() => {
    const onResize = () => {
      setPosition((prev) => clampPositionToViewport(prev.x, prev.y));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      const next = clampPositionToViewport(
        dragStartRef.current.left + dx,
        dragStartRef.current.top + dy
      );
      setPosition(next);
    };
    const onUp = () => {
      setIsDragging(false);
      const pos = clampPositionToViewport(positionRef.current.x, positionRef.current.y);
      setPosition(pos);
      onPositionChange(pos);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
      } catch {
        // ignore
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging, onPositionChange]);

  const handleAccept = useCallback(
    async (request: CallRequest) => {
      const socketService = getSocketService();
      if (!socketService) {
        dispatch(addErrorToast("Real-time connection unavailable. Please refresh."));
        return;
      }
      try {
        setProcessingId(request.visitorId);
        socketService.stopNotificationSound();
        if (request.roomUrl && request.callId) {
          socketService.answerCallRequest({
            callId: request.callId,
            roomUrl: request.roomUrl,
            roomName: request.roomName,
            type: request.type,
            visitorId: request.visitorId,
          });
        } else {
          socketService.initiateCall(request.visitorId, request.type);
        }
        dispatch(
          addSuccessToast(`Connecting ${request.type === "video" ? "video" : "voice"} call…`)
        );
      } catch (err) {
        dispatch(addErrorToast(err instanceof Error ? err.message : "Failed to start call"));
      } finally {
        setProcessingId(null);
      }
    },
    [dispatch]
  );

  const handleDismissRequest = useCallback(
    (visitorId: string) => {
      getSocketService()?.stopNotificationSound();
      dispatch(removeCallRequest(visitorId));
    },
    [dispatch]
  );

  const handleCallVisitor = useCallback(
    async (visitor: { id: string }) => {
      const result = await initiateCall(visitor.id, "video");
      if (result.success) {
        dispatch(addSuccessToast("Connecting video call with visitor…"));
      } else if (result.error) {
        dispatch(addErrorToast(result.error));
      }
    },
    [initiateCall, dispatch]
  );

  const displayVisitors = activeVisitors.slice(0, 8);

  return (
    <div
      ref={panelRef}
      className="fixed z-100 flex flex-col rounded-2xl border border-border bg-card shadow-2xl will-change-transform"
      style={{
        left: position.x,
        top: position.y,
        width: PANEL_WIDTH,
        minHeight: PANEL_MIN_HEIGHT,
        maxHeight: `min(${PANEL_MAX_HEIGHT}vh, 720px)`,
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      {/* Drag header */}
      <div
        role="presentation"
        onMouseDown={handleDragStart}
        className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border cursor-grab active:cursor-grabbing select-none rounded-t-2xl bg-light-1/50"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-base font-semibold text-text truncate">Live deck</span>
          {sortedRequests.length > 0 && (
            <Badge variant="info" size="sm">
              {sortedRequests.length} waiting
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab(activeTab === "calls" ? "visitors" : "calls")}
            className="p-2 rounded-lg hover:bg-light-2 text-gray-500 hover:text-text transition-colors"
            aria-label={activeTab === "calls" ? "Show visitors" : "Show call requests"}
          >
            {activeTab === "calls" ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            )}
          </button>
          <button
            type="button"
            onClick={handleMinimize}
            className="p-2 rounded-lg hover:bg-light-2 text-gray-500 hover:text-text transition-colors"
            aria-label="Minimize deck"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
        </div>
      </div>

      {/* Tabs + content */}
      <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
        <div className="flex border-b border-border px-2">
          <button
            type="button"
            onClick={() => setActiveTab("calls")}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === "calls"
                ? "text-pri border-b-2 border-pri bg-pri/5"
                : "text-gray-500 hover:text-text"
            }`}
          >
            Call requests
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("visitors")}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === "visitors"
                ? "text-pri border-b-2 border-pri bg-pri/5"
                : "text-gray-500 hover:text-text"
            }`}
          >
            Visitors ({activeVisitors.length})
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain p-3">
          {activeTab === "calls" && (
            <>
              {sortedRequests.length === 0 ? (
                <EmptyState
                  title="No live requests"
                  description="Visitors will appear here when they request a video or voice call from the widget."
                />
              ) : (
                <div className="space-y-3">
                  {sortedRequests.map((request) => (
                    <div
                      key={request.requestId}
                      className="p-3 rounded-xl border border-border bg-light-1/50 hover:border-pri/40 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge
                              variant={request.type === "video" ? "info" : "default"}
                              size="sm"
                            >
                              {request.type === "video" ? "Video" : "Voice"}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {formatTimeAgo(request.requestedAt)}
                            </span>
                          </div>
                          <p className="text-sm text-text mt-1 line-clamp-2">
                            {request.message || "Visitor wants to talk."}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDismissRequest(request.visitorId)}
                          className="shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-light-2"
                          aria-label="Dismiss"
                        >
                          ×
                        </button>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleAccept(request)}
                          disabled={processingId === request.visitorId}
                          className="flex-1 px-3 py-2 bg-linear-to-r from-pri to-sec text-white rounded-lg hover:opacity-90 text-sm font-medium disabled:opacity-60 transition-opacity"
                        >
                          {processingId === request.visitorId ? "Connecting…" : "Accept"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDismissRequest(request.visitorId)}
                          className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-light-2 transition-colors"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === "visitors" && (
            <>
              {displayVisitors.length === 0 ? (
                <EmptyState
                  title="No active visitors"
                  description="Visitors on your site will appear here in real time."
                />
              ) : (
                <ul className="space-y-2">
                  {displayVisitors.map((visitor) => {
                    const name =
                      [visitor.name, (visitor.metadata as any)?.name].find(
                        (v) => typeof v === "string" && v?.trim()
                      ) || "Visitor";
                    const page = visitor.page_title || "Untitled";
                    return (
                      <li
                        key={visitor.id}
                        className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-border hover:border-pri/40 hover:bg-light-1/50 transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-text truncate text-sm">{name}</div>
                          <div className="text-xs text-gray-500 truncate">{page}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCallVisitor(visitor)}
                          className="shrink-0 px-3 py-1.5 bg-linear-to-r from-pri to-sec text-white rounded-lg hover:opacity-90 text-xs font-medium transition-opacity"
                        >
                          Call
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {activeVisitors.length > 8 && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Showing 8 of {activeVisitors.length} · Open Live page for full list
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ----- Backdrop (visual only; deck stays open until user minimizes) -----
function LiveDeckBackdrop() {
  const backdropRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (!backdropRef.current) return;
      gsap.fromTo(
        backdropRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.18, ease: "power2.out" }
      );
    },
    { scope: backdropRef, dependencies: [] }
  );

  return <div ref={backdropRef} className="fixed inset-0 z-99 pointer-events-none" aria-hidden />;
}

// ----- Main export: trigger + portal panel -----
export default function LiveDeck({ billingBlocksLive }: { billingBlocksLive: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(loadStoredPosition);
  const callRequests = useAppSelector(selectCallRequests);
  const activeVisitors = useAppSelector(selectActiveVisitors);
  const totalCount = callRequests.length + activeVisitors.length;
  const openDeck = useCallback(() => {
    setPosition(() => {
      const stored = loadStoredPosition();
      const hasStored = stored.x !== 0 || stored.y !== 0;
      return hasStored ? stored : getDefaultPosition();
    });
    setIsOpen(true);
  }, []);

  const closeDeck = useCallback(() => {
    setIsOpen(false);
  }, []);

  const panel = isOpen && (
    <>
      <LiveDeckBackdrop />
      <LiveDeckPanel
        onClose={closeDeck}
        initialPosition={position}
        onPositionChange={setPosition}
      />
    </>
  );

  return (
    <>
      {billingBlocksLive ? (
        <Tooltip content="Billing required">
          <div className="size-11 rounded-full bg-light-1 hover:bg-light-2 flex items-center justify-center transition-colors">
            <svg
              className="w-5 h-5 text-text"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
          </div>
        </Tooltip>
      ) : (
        <div className="relative shrink-0">
          <Tooltip content={isOpen ? "" : "Open live deck (calls & visitors)"}>
            <button
              type="button"
              onClick={openDeck}
              className="size-11 rounded-full bg-light-1 hover:bg-light-2 flex items-center justify-center transition-colors"
              aria-label="Open live deck (calls & visitors)"
            >
              <svg
                className="w-5 h-5 text-text"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </button>
          </Tooltip>

          {totalCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-pri text-white text-[10px] font-semibold pointer-events-none">
              {totalCount > 99 ? "99+" : totalCount}
            </span>
          )}
        </div>
      )}
      {typeof document !== "undefined" && createPortal(panel, document.body)}
    </>
  );
}
